import React, { useEffect, useState } from "react"
import { useMatch } from "react-router-dom"
import PropTypes from "prop-types"
import { oldRoutes } from "router/old-routes"
import { Button, Dropdown, Space, Layout, Row, Col } from "antd"
import styles from "./TripFiltersNavigation.module.css"
import { DownOutlined } from "@ant-design/icons"
import { useScrollToActive } from "hooks/useScrollToActive"
import { useDispatch, useSelector } from "react-redux"
import { getFilterOptions, setPriceRange, setLanguage, setSkillLevel, setExperiences, setOther, getCategories } from "store/trips"
import { firstLoadingSelector } from "modules/loader-watchers/selectors"
import { useTranslation } from "react-i18next"
import PriceFilterDropdown from "components/filters/PriceFilterDropdown"
import LanguageFilterDropdown from "components/filters/LanguageFilterDropdown"
import SkillLevelFilterDropdown from "components/filters/SkillLevelFilterDropdown"
import ExperiencesFilterDropdown from "components/filters/ExperiencesFilterDropdown"
import OtherFilterDropdown from "components/filters/OtherFilterDropdown"
import FilterBadge from "components/FilterBadge"
import ClearFiltersButton from "components/common/ClearFiltersButton"
import useSearchFilter from "hooks/useSearchFilter"
import { TRIP_FILTERS } from "constants/trip"

export default function TripFiltersNavigation({ className, visible = false, fluid = true }) {
  const dispatch = useDispatch()
  const navigationRef = useScrollToActive()
  const isTripsPage = useMatch(oldRoutes.tripsPath())
  const filterOptionsFirstLoading = useSelector(firstLoadingSelector("trips.filter_options"))
  const { t } = useTranslation()
  const [categoriesData, setCategoriesData] = useState([])
  const [, updateSearchFilters] = useSearchFilter({ accessAttributes: TRIP_FILTERS })
  // selectors for filter state
  const priceRange = useSelector((state) => state.trips.priceRange)
  const language = useSelector((state) => state.trips.language)
  const skillLevel = useSelector((state) => state.trips.skillLevel)
  const experiences = useSelector((state) => state.trips.experiences)
  const other = useSelector((state) => state.trips.other)
  const filterOptions = useSelector((state) => state.trips?.filter_options?.lists || {})

  const classes = ["trip-filters-navigation", "ant-layout", "ant-layout-has-sider"]
  if (className) classes.push(className)

  useEffect(() => {
    if (filterOptionsFirstLoading) dispatch(getFilterOptions())
  }, [filterOptionsFirstLoading, dispatch]) //eslint-disable-line

  useEffect(() => {
    dispatch(getCategories()).then((res) => {
      setCategoriesData(res)
    })
  }, [dispatch])

  const priceLabel = priceRange?.length > 0 ? `${t("filterTags.price")} $${priceRange[0]} - $${priceRange[1]}` : t("filterTags.price")

  const absolutePriceRange = filterOptions?.price_range
  const priceFiltered =
    Array.isArray(priceRange) &&
    priceRange.length === 2 &&
    Array.isArray(absolutePriceRange) &&
    absolutePriceRange.length === 2 &&
    (priceRange[0] !== absolutePriceRange[0] || priceRange[1] !== absolutePriceRange[1])
  const hasFilters = priceFiltered || language.length > 0 || skillLevel.length > 0 || experiences.length > 0 || other.length > 0

  return isTripsPage || visible ? (
    <Layout className={`${classes.join(" ")} ${styles.layoutWrapper}`}>
      <div ref={navigationRef} className="py-m">
        <Row gutter={[16, 16]} align="middle" className={styles.nowrapRow}>
          <Col flex="auto" className={styles.filterWrapper}>
            <Space size="small" className="filterWrappers">
              <Dropdown
                overlay={<PriceFilterDropdown value={priceRange} onChange={(val) => dispatch(setPriceRange(val))} />}
                trigger={["click"]}
                key="Price"
              >
                <Button shape="round" size="middle" className={styles.filterButton}>
                  {priceLabel} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                overlay={
                  <LanguageFilterDropdown
                    value={language}
                    onChange={(val) => {
                      dispatch(setLanguage(val))
                      updateSearchFilters({ language: val })
                    }}
                  />
                }
                trigger={["click"]}
                key="Language"
              >
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={language.length} /> {t("filterTags.language")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                overlay={<SkillLevelFilterDropdown value={skillLevel} onChange={(val) => dispatch(setSkillLevel(val))} />}
                trigger={["click"]}
                key="Skill Level"
              >
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={skillLevel.length} /> {t("filterTags.skill_level")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                overlay={
                  <ExperiencesFilterDropdown
                    value={experiences}
                    onChange={(val) => dispatch(setExperiences(val))}
                    categories={categoriesData}
                  />
                }
                trigger={["click"]}
                key="Experiences"
              >
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={experiences.length} /> {t("filterTags.experiences")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                overlay={<OtherFilterDropdown value={other} onChange={(val) => dispatch(setOther(val))} />}
                trigger={["click"]}
                key="Other"
              >
                <Button shape="round" size="middle" className={styles.filterButton}>
                  <FilterBadge count={other.length} /> {t("filterTags.other")} <DownOutlined />
                </Button>
              </Dropdown>
              {hasFilters && <ClearFiltersButton className={styles.clearFiltersButton} />}
            </Space>
          </Col>
        </Row>
      </div>
    </Layout>
  ) : null
}

TripFiltersNavigation.propTypes = {
  className: PropTypes.string,
  visible: PropTypes.bool,
  fluid: PropTypes.bool
}
